import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import './dashboard.css';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return String(iso);
  }
}

function formatField(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

export default function BusinessDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setError('');
      setLoading(true);
      try {
        const [
          { data: biz, error: bizErr },
          { data: conv, error: convErr },
          { data: leadRows, error: leadErr },
        ] = await Promise.all([
          supabase
            .from('businesses')
            .select(
              'id, name, owner_email, twilio_number, website_url, services, hours, price_range, is_active, created_at'
            )
            .eq('id', id)
            .maybeSingle(),
          supabase
            .from('conversations')
            .select('id, customer_phone, summary, status, created_at')
            .eq('business_id', id)
            .order('created_at', { ascending: false }),
          supabase
            .from('leads')
            .select(
              'id, customer_name, customer_phone, what_they_need, urgency_level, best_callback_time, status, created_at'
            )
            .eq('business_id', id)
            .order('created_at', { ascending: false }),
        ]);

        if (bizErr) throw bizErr;
        if (convErr) throw convErr;
        if (leadErr) throw leadErr;

        if (!cancelled) {
          setBusiness(biz);
          setConversations(conv ?? []);
          setLeads(leadRows ?? []);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message ?? 'Failed to load business.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (id) load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  return (
    <div className="page">
      <div className="page-toolbar">
        <button type="button" className="back-button" onClick={() => navigate('/businesses')}>
          ← Back to businesses
        </button>
      </div>
      <h1 className="page-title">Business details</h1>
      {error ? <p className="page-error">{error}</p> : null}

      {loading ? (
        <p className="table-muted">Loading…</p>
      ) : !business ? (
        <p className="page-error">Business not found.</p>
      ) : (
        <>
          <dl className="detail-grid">
            <div className="detail-row">
              <dt>Business name</dt>
              <dd>{formatField(business.name)}</dd>
            </div>
            <div className="detail-row">
              <dt>Owner email</dt>
              <dd>{formatField(business.owner_email)}</dd>
            </div>
            <div className="detail-row">
              <dt>Twilio number</dt>
              <dd>{formatField(business.twilio_number)}</dd>
            </div>
            <div className="detail-row">
              <dt>Website URL</dt>
              <dd>
                {business.website_url ? (
                  <a href={business.website_url} target="_blank" rel="noopener noreferrer" className="detail-link">
                    {business.website_url}
                  </a>
                ) : (
                  '—'
                )}
              </dd>
            </div>
            <div className="detail-row">
              <dt>Services</dt>
              <dd className="detail-multiline">{formatField(business.services)}</dd>
            </div>
            <div className="detail-row">
              <dt>Hours</dt>
              <dd className="detail-multiline">{formatField(business.hours)}</dd>
            </div>
            <div className="detail-row">
              <dt>Price range</dt>
              <dd>{formatField(business.price_range)}</dd>
            </div>
            <div className="detail-row">
              <dt>Active</dt>
              <dd>{business.is_active ? 'Yes' : 'No'}</dd>
            </div>
            <div className="detail-row">
              <dt>Created at</dt>
              <dd>{formatDate(business.created_at)}</dd>
            </div>
          </dl>

          <h2 className="section-title">Recent conversations</h2>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer phone</th>
                  <th>Summary</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {conversations.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="table-muted">
                      No conversations yet.
                    </td>
                  </tr>
                ) : (
                  conversations.map((row) => (
                    <tr key={row.id}>
                      <td>{row.customer_phone ?? '—'}</td>
                      <td>{row.summary ?? '—'}</td>
                      <td>{row.status ?? '—'}</td>
                      <td>{formatDate(row.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <h2 className="section-title">Recent leads</h2>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer name</th>
                  <th>Customer phone</th>
                  <th>What they need</th>
                  <th>Urgency</th>
                  <th>Best callback time</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="table-muted">
                      No leads yet.
                    </td>
                  </tr>
                ) : (
                  leads.map((row) => (
                    <tr key={row.id}>
                      <td>{row.customer_name ?? '—'}</td>
                      <td>{row.customer_phone ?? '—'}</td>
                      <td>{row.what_they_need ?? '—'}</td>
                      <td>{row.urgency_level ?? '—'}</td>
                      <td>{row.best_callback_time ?? '—'}</td>
                      <td>{row.status ?? '—'}</td>
                      <td>{formatDate(row.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
